import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useConsult } from "@/context/ConsultContext";

function renderContent(content) {
  return content.split("\n").filter(Boolean).map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i}>{line.replace("## ", "")}</h2>;
    return <p key={i}>{line}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { openConsult } = useConsult();

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-24 min-h-screen text-center" data-testid="blog-post-notfound">
        <h1 className="font-head font-extrabold text-3xl text-slate-900">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-[var(--dm-purple)] font-bold">← Back to blog</Link>
      </main>
    );
  }

  if (!post) return <main className="pt-40 pb-24 min-h-screen text-center text-slate-400">Loading…</main>;

  const date = post.created_at ? new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <main className="pt-28 pb-24 min-h-screen bg-white" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-head font-bold text-slate-500 hover:text-[var(--dm-purple)] transition-colors mb-8">
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        <span className="rounded-full bg-purple-100 text-[var(--dm-purple)] px-3 py-1 text-xs font-head font-bold">{post.category}</span>
        <h1 className="font-head font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-900 mt-5 leading-[1.05]">{post.title}</h1>

        <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-slate-500">
          <span className="font-head font-bold text-slate-700">{post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar size={14} /> {date}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> {post.read_time}</span>
        </div>

        <div className="mt-8 rounded-2xl overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-72 sm:h-96 object-cover" />
        </div>

        <div className="blog-content mt-10">{renderContent(post.content)}</div>

        <div className="mt-14 rounded-[1.75rem] p-8 text-center" style={{ background: "linear-gradient(120deg,#6C2BD9,#2563EB)" }}>
          <h3 className="font-head font-extrabold text-2xl text-white">Ready to grow your business?</h3>
          <p className="text-white/80 mt-2">Book a free consultation and get a tailored strategy.</p>
          <button onClick={openConsult} data-testid="blog-post-cta" className="mt-5 rounded-full bg-white text-slate-900 px-7 py-3.5 font-head font-bold transition-transform hover:-translate-y-0.5">
            Book a Free Consultation
          </button>
        </div>
      </article>
    </main>
  );
}
